import React from 'react';
import { PaystackButton as PaystackPayButton } from 'react-paystack';
import { Crown, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PAYSTACK, PLAN_DETAILS } from '../../lib/constants';
import { verifyPayment, updateSubscription } from '../../services/paymentService';
import toast from 'react-hot-toast';

const PaystackButton = ({ planType = 'MONTHLY', onSuccess, className }) => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const planDetails = PLAN_DETAILS[planType];
  const amount = planDetails.price;

  const config = {
    reference: `OC_${planType}_${new Date().getTime()}_${user.id.slice(0, 8)}`,
    email: user.email,
    amount: amount * 100, // Convert to kobo
    publicKey: PAYSTACK.PUBLIC_KEY,
    metadata: {
      user_id: user.id,
      plan_type: planType,
      duration_days: planDetails.duration,
      custom_fields: [
        {
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: user.user_metadata?.full_name || user.email,
        },
        {
          display_name: 'Plan',
          variable_name: 'plan',
          value: planDetails.name,
        },
      ],
    },
    channels: PAYSTACK.CHANNELS,
  };

const handleSuccess = async (reference) => {
    setLoading(true);
    const toastId = toast.loading('Verifying payment...');

    try {
      // Extract the reference string
      const refString = typeof reference === 'object' ? reference.reference : reference;

      // Safely attempt client-side sync without stopping the process if it fails
      try {
        const verification = await verifyPayment(refString);
        if (verification?.success) {
          await updateSubscription(user.id, verification.data);
        }
      } catch (clientErr) {
        // Webhook handles the DB update in the background if client fails
        console.warn('Webhook will process DB update:', clientErr);
      }

      // Refresh profile to pick up the premium status from the webhook
      await refreshProfile();

      toast.dismiss(toastId);
      toast.success(`🎉 Payment Successful! Welcome to Premium ${planDetails.name}!`, {
        duration: 5000,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Fallback message so users never see "failed" when payment succeeds
      toast.dismiss(toastId);
      toast.success('🎉 Payment received! Updating your subscription status...');
      console.error('Payment notice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    toast.error('Payment cancelled');
  };

  const componentProps = {
    ...config,
    text: loading ? 'Processing...' : `Pay ₦${amount.toLocaleString()}`,
    onSuccess: handleSuccess,
    onClose: handleClose,
  };

  return (
    <PaystackPayButton
      {...componentProps}
      className={`flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 sm:py-4 
        bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 
        text-white font-semibold rounded-lg sm:rounded-xl transition-all shadow-lg 
        disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base md:text-lg ${className || ''}`}
      disabled={loading}
    >
      {loading ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Processing...</span>
        </>
      ) : (
        <>
          <Crown className="w-5 h-5" />
          <span>Upgrade to {planDetails.name} - ₦{amount.toLocaleString()}</span>
        </>
      )}
    </PaystackPayButton>
  );
};

export default PaystackButton;